import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Bike, Link2, CheckCircle2, RefreshCw } from "lucide-react";

const CHANNELS = [
  { key: "zomato", name: "Zomato", color: "#E23744", desc: "Live partner API pull + webhook ingest into Orders." },
  { key: "swiggy", name: "Swiggy", color: "#FC8019", desc: "Live partner API pull + webhook ingest into Orders." },
];

export default function Channels() {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState({});
  const [pulling, setPulling] = useState(false);
  const { data } = useQuery({ queryKey: ["channels"], queryFn: async () => (await api.get("/integrations/channels")).data });
  useEffect(() => { if (data) setCfg(data); }, [data]);

  const save = async (key) => {
    const c = cfg[key] || {};
    await api.put("/integrations/channels", {
      channel: key,
      connected: !!c.connected,
      outlet_id: c.outlet_id || "",
      api_key: c.api_key || "",
      api_base_url: c.api_base_url || "",
      webhook_secret: c.webhook_secret || "",
      mode: c.mode === "live" ? "live" : "demo",
    });
    toast.success(`${key} settings saved`);
    qc.invalidateQueries({ queryKey: ["channels"] });
  };
  const setField = (key, field, val) => setCfg({ ...cfg, [key]: { ...(cfg[key] || {}), [field]: val } });

  const pull = async () => {
    setPulling(true);
    try {
      const { data: r } = await api.post("/integrations/pull-delivery");
      const mode = r.mode ? ` (${r.mode})` : "";
      toast.success(`Pulled ${r.pulled} order(s)${mode}`);
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Connect a channel first"); }
    finally { setPulling(false); }
  };

  const webhookHint = (key) => {
    const secret = (cfg[key] || {}).webhook_secret;
    if (!secret) return "Set a webhook secret, then partners POST to /api/webhook/channels/{channel}/{secret}";
    return `Webhook: POST /api/webhook/channels/${key}/${secret}`;
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Delivery Channels" subtitle="Live Zomato & Swiggy APIs, or demo sync when credentials are missing">
        <Button onClick={pull} disabled={pulling} data-testid="channels-sync" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white">
          <RefreshCw className={`w-4 h-4 mr-1 ${pulling ? "animate-spin" : ""}`} /> Sync Now
        </Button>
      </PageHeader>

      <div className="grid md:grid-cols-2 gap-5 max-w-4xl">
        {CHANNELS.map((ch) => {
          const c = cfg[ch.key] || {};
          const live = c.mode === "live" && c.api_key && c.api_base_url;
          return (
            <div key={ch.key} className="kul-card p-6" data-testid={`channel-${ch.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${ch.color}1a` }}>
                    <Bike className="w-5 h-5" style={{ color: ch.color }} />
                  </div>
                  <div>
                    <div className="font-display font-700 text-lg">{ch.name}</div>
                    <div className="flex gap-1 mt-0.5">
                      {c.connected
                        ? <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle2 className="w-3 h-3" />Connected</Badge>
                        : <Badge className="bg-neutral-100 text-neutral-500">Not connected</Badge>}
                      {c.connected && (
                        <Badge className={live ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>
                          {live ? "Live API" : "Demo"}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <Switch checked={!!c.connected} onCheckedChange={(v) => setField(ch.key, "connected", v)} data-testid={`toggle-${ch.key}`} />
              </div>
              <p className="text-sm text-neutral-500 mt-3">{ch.desc}</p>
              <div className="space-y-3 mt-4">
                <div>
                  <Label>Mode</Label>
                  <select
                    className="w-full h-10 rounded-lg border px-2 text-sm"
                    value={c.mode === "live" ? "live" : "demo"}
                    onChange={(e) => setField(ch.key, "mode", e.target.value)}
                    data-testid={`mode-${ch.key}`}
                  >
                    <option value="demo">Demo (sample orders)</option>
                    <option value="live">Live partner API</option>
                  </select>
                </div>
                <div><Label>Outlet / Restaurant ID</Label><Input value={c.outlet_id || ""} onChange={(e) => setField(ch.key, "outlet_id", e.target.value)} placeholder={`${ch.name} outlet id`} data-testid={`outlet-${ch.key}`} /></div>
                <div><Label>Partner API Key</Label><Input value={c.api_key || ""} onChange={(e) => setField(ch.key, "api_key", e.target.value)} placeholder="••••••••" data-testid={`apikey-${ch.key}`} /></div>
                <div><Label>API base URL (live)</Label><Input value={c.api_base_url || ""} onChange={(e) => setField(ch.key, "api_base_url", e.target.value)} placeholder="https://partner-api.example.com/v1" data-testid={`base-${ch.key}`} /></div>
                <div><Label>Webhook secret</Label><Input value={c.webhook_secret || ""} onChange={(e) => setField(ch.key, "webhook_secret", e.target.value)} placeholder="shared secret / ingest token" data-testid={`webhook-${ch.key}`} /></div>
                <p className="text-[11px] text-neutral-400 break-all">{webhookHint(ch.key)}</p>
              </div>
              <Button onClick={() => save(ch.key)} data-testid={`save-${ch.key}`} className="w-full mt-4" style={{ background: ch.color, color: "#fff" }}>
                <Link2 className="w-4 h-4 mr-1" /> {c.connected ? "Update Connection" : "Connect"}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-neutral-400 mt-5 max-w-2xl">
        Live mode calls <code>GET {"{api_base_url}"}/orders</code> with your API key, and accepts partner webhooks.
        Demo mode still works without partner credentials so you can test kitchen / SLA flows.
      </p>
    </div>
  );
}
